import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';

interface Props {
  open: boolean;
  /** Existing schedule, as a `datetime-local` string. */
  initialValue: string;
  isSaving: boolean;
  /** Whether this note will actually email anyone when it publishes. */
  sendEmail: boolean;
  onClose: () => void;
  onConfirm: (scheduledFor: string) => Promise<void>;
}

/** `datetime-local` wants `YYYY-MM-DDTHH:mm` in local time, not an ISO string. */
const toLocalInputValue = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
};

/**
 * Picks the publish time for a scheduled release note.
 *
 * Split out of the editor so the action bar can stay four fixed buttons. It also
 * gives the date somewhere to be validated: the previous inline field accepted a
 * past date silently, which the cron would then publish on its next tick.
 */
const ScheduleDialog: React.FC<Props> = ({
  open,
  initialValue,
  isSaving,
  sendEmail,
  onClose,
  onConfirm,
}) => {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    // Default to an hour out — a sensible "soon, but not this second".
    const fallback = new Date(Date.now() + 60 * 60 * 1000);
    setValue(initialValue || toLocalInputValue(fallback));
    setError(null);
  }, [open, initialValue]);

  const handleConfirm = async () => {
    if (!value) {
      setError('Pick a date and time.');
      return;
    }
    const when = new Date(value);
    if (Number.isNaN(when.getTime())) {
      setError('That is not a valid date.');
      return;
    }
    if (when.getTime() <= Date.now()) {
      setError('Pick a time in the future — use "Publish now" to send immediately.');
      return;
    }
    setError(null);
    await onConfirm(value);
  };

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Schedule release note</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The note publishes automatically at this time.
        </Typography>

        <TextField
          label="Publish at"
          type="datetime-local"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          size="small"
          fullWidth
          disabled={isSaving}
          InputLabelProps={{ shrink: true }}
          helperText="Your local time."
        />

        {/* Publishing without email is valid but rarely intended — say so here
            rather than letting the admin discover it from an empty inbox. */}
        {!sendEmail && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            "Send email" is off, so this will only show the in-app popup — no one
            will be emailed. Turn it on before scheduling if you want mail sent.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={isSaving} color="inherit">
          Cancel
        </Button>
        <Button onClick={() => void handleConfirm()} disabled={isSaving} variant="contained">
          {isSaving ? 'Scheduling…' : 'Schedule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleDialog;
